/**
 * src/lib/data/dashboard.ts — read wrappers for the shared state + HubSpot
 * actuals (Phase 7 sub-step 2).
 *
 * Both endpoints exist on the worker and are open per §1.4 (view-password
 * gate is enough). These thin wrappers exist so the UserProfile's personal
 * view can render an AE's monthly numbers + deals without inlining the
 * fetch shape in the view.
 *
 * Schema is `any` on purpose — shared_state has accumulated organically and
 * is owned by the legacy state-storage module. Typing it here would add a
 * stale duplicate. Consumers project the fields they care about.
 */

export interface DashboardFetchOptions {
  workerUrl: string;
  viewPassword: string;
  signal?: AbortSignal;
}

export class DashboardApiError extends Error {
  status: number;
  body: any;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.name = "DashboardApiError";
    this.status = status;
    this.body = body;
  }
}

async function jsonOrThrow(resp: Response, op: string): Promise<any> {
  let body: any = null;
  try { body = await resp.json(); } catch {}
  if (!resp.ok) {
    throw new DashboardApiError(
      `${op} failed: ${resp.status} ${body?.error || resp.statusText}`,
      resp.status,
      body,
    );
  }
  return body;
}

/** GET /api/state → the full shared_state JSON (or null if KV is empty). */
export async function getSharedState(opts: DashboardFetchOptions): Promise<any | null> {
  const resp = await fetch(`${opts.workerUrl}/api/state`, {
    headers: { "X-View-Password": opts.viewPassword },
    signal: opts.signal,
  });
  return await jsonOrThrow(resp, "getSharedState");
}

/** GET /api/actuals → aggregated monthly actuals + deals from HubSpot. */
export async function getActuals(opts: DashboardFetchOptions): Promise<any> {
  const resp = await fetch(`${opts.workerUrl}/api/actuals`, {
    headers: { "X-View-Password": opts.viewPassword },
    signal: opts.signal,
  });
  return await jsonOrThrow(resp, "getActuals");
}
